import { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Sparkles, Loader2, CheckCircle2, MailWarning, ArrowRight } from "lucide-react";

const dashFor = (role, after) =>
  role === "super_admin" ? "/superadmin" : role === "admin" ? "/admin" : role === "recruiter" ? "/recruiter" : (after || "/candidate");

export default function VerifyEmail() {
  const { verifyEmail, resendVerification } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [status, setStatus] = useState("verifying"); // verifying | success | error
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [resent, setResent] = useState(false);
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;
    if (!token) { setStatus("error"); return; }
    (async () => {
      const res = await verifyEmail(token);
      if (res.ok) {
        setStatus("success");
        const after = localStorage.getItem("hs_after_verify");
        localStorage.removeItem("hs_after_verify");
        const to = dashFor(res.user.role, after);
        setTimeout(() => nav(to, { replace: true }), 1500);
      } else {
        setStatus("error");
      }
    })();
  }, [token, verifyEmail, nav]);

  const resend = async () => {
    if (!email || !email.includes("@")) { toast.error("Enter the email you signed up with"); return; }
    setBusy(true);
    await resendVerification(email);
    setBusy(false);
    setResent(true);
    toast.success("If that account needs verifying, a fresh link is on its way.");
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#050807] text-white p-6" data-testid="verify-email-page">
      <div className="pointer-events-none fixed -top-40 left-1/4 h-[34rem] w-[34rem] rounded-full bg-emerald-500/15 blur-[130px]" />
      <div className="relative w-full max-w-md rounded-2xl border border-white/10 bg-[#111820] p-8 text-center space-y-6">
        <Link to="/" className="inline-flex items-center gap-2.5 justify-center" data-testid="verify-brand">
          <div className="h-9 w-9 rounded-lg bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center shadow-lg shadow-emerald-500/30"><Sparkles className="h-5 w-5 text-white" /></div>
          <span className="font-display font-bold text-lg">HireStream</span>
        </Link>

        {status === "verifying" && (
          <div className="space-y-4 py-4" data-testid="verify-state-verifying">
            <Loader2 className="h-10 w-10 text-emerald-400 animate-spin mx-auto" />
            <h1 className="text-xl font-display font-bold">Verifying your email…</h1>
            <p className="text-sm text-slate-400">Hang tight, this only takes a moment.</p>
          </div>
        )}

        {status === "success" && (
          <div className="space-y-4 py-4" data-testid="verify-state-success">
            <div className="h-14 w-14 rounded-2xl bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center mx-auto">
              <CheckCircle2 className="h-7 w-7 text-emerald-400" />
            </div>
            <h1 className="text-2xl font-display font-bold tracking-tight">Email verified!</h1>
            <p className="text-sm text-slate-400">You're all set — taking you to your dashboard…</p>
          </div>
        )}

        {status === "error" && (
          <div className="space-y-5" data-testid="verify-state-error">
            <div className="h-14 w-14 rounded-2xl bg-amber-500/15 border border-amber-500/30 flex items-center justify-center mx-auto">
              <MailWarning className="h-7 w-7 text-amber-400" />
            </div>
            <div className="space-y-2">
              <h1 className="text-2xl font-display font-bold tracking-tight">Link invalid or expired</h1>
              <p className="text-sm text-slate-400 leading-relaxed">This verification link is no longer valid. Enter your email below and we'll send a fresh one.</p>
            </div>
            <div className="space-y-2 text-left">
              <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@email.com" data-testid="verify-resend-email" className="bg-white/[0.03] border-white/15 text-white" />
              <Button onClick={resend} disabled={busy} data-testid="verify-resend-submit" className="w-full">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Resend verification link"}
              </Button>
              {resent && <p className="text-xs text-emerald-300 text-center" data-testid="verify-resent-note">Sent — check your inbox and spam folder. The link expires in 24 hours.</p>}
            </div>
            <Link to="/login" className="inline-flex items-center gap-1.5 text-sm text-slate-400 hover:text-white transition-colors" data-testid="verify-to-login">
              Back to sign in <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
